import { useEffect, useState, useSyncExternalStore } from "react";
import type { Equation } from "@src/features/equation-editor/equation";
import type { Dimension } from "@src/math/types";
import type { AnimationProgress } from "@src/features/playback/animation";

type AnimationClockState =
  | { readonly status: "idle" | "paused" }
  | { readonly status: "playing"; readonly runningSinceMs: number };

export interface AnimationSource {
  readonly getSnapshot: () => AnimationClockState;
  readonly subscribe: (listener: () => void) => () => void;
  readonly getProgress: <D extends Dimension>(
    equation: Equation<D>,
    now: number,
  ) => AnimationProgress | null;
}

export function useAnimationProgress<D extends Dimension>(
  source: AnimationSource,
  equation: Equation<D>,
) {
  const animation = useSyncExternalStore(
    source.subscribe,
    source.getSnapshot,
    source.getSnapshot,
  );
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (animation.status !== "playing") return;
    let frame = requestAnimationFrame(function tick(time) {
      setNow(time);
      frame = requestAnimationFrame(tick);
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [animation.status]);
  const frameNow =
    animation.status === "playing" && now < animation.runningSinceMs
      ? animation.runningSinceMs
      : now;
  return source.getProgress(equation, frameNow);
}
